import * as api from './bff_helper';

/**
 * Transform campaign data from backend format to frontend format
 *
 * Key transformations:
 * - cashback: percentage (0-1) → display (0-100), fixed (cents) → display (decimal)
 * - rescue: 0-1 → display (0-100)
 * - maximum_rescue: cents (integer) → display (decimal)
 * - timestamps: seconds → ISO string
 */
export const transformBackendCampaignToFrontend = (campaign) => {
  if (!campaign || Object.keys(campaign).length === 0) return null;

  // Determine if campaign is currently active based on dates
  const now = new Date();
  const startDate = campaign.start_value
    ? new Date(campaign.start_value * 1000)
    : null;
  const endDate = campaign.end_value
    ? new Date(campaign.end_value * 1000)
    : null;

  let isCurrentlyActive = false;
  if (campaign.status === 'active' && startDate && endDate) {
    isCurrentlyActive = now >= startDate && now <= endDate;
  } else if (campaign.status === 'active' && startDate && !endDate) {
    isCurrentlyActive = now >= startDate;
  }

  // Transform cashback value based on type
  // Backend: percentage = 0-1 (e.g., 0.20 for 20%), fixed = cents (e.g., 2000 for R$ 20.00)
  // Frontend: percentage = 0-100 (e.g., 20 for 20%), fixed = decimal (e.g., 20.00 for R$ 20.00)
  const cashbackType = campaign.strategy?.cashback_type || 'percentage';
  const backendCashback = parseFloat(campaign.strategy?.cashback) || 0;

  let cashbackValue;
  if (cashbackType === 'percentage') {
    // Backend: 0.20 → Frontend: 20
    cashbackValue = backendCashback * 100;
  } else {
    // Backend: 2000 (cents) → Frontend: 20.00
    cashbackValue = backendCashback / 100;
  }

  // Transform rescue value (maximum discount percentage)
  // Backend: 0-1 (e.g., 0.10 for 10%)
  // Frontend: 0-100 (e.g., 10 for 10%)
  const backendRescue = parseFloat(campaign.strategy?.rescue) || 0;
  const rescueValue = backendRescue * 100;

  // Transform maximum_rescue (maximum redemption value in R$)
  // Backend: cents (integer, e.g., 98765400 for R$ 987,654.00)
  // Frontend: decimal (e.g., 987654.00 for R$ 987,654.00)
  const backendMaxRescue = parseFloat(campaign.strategy?.maximum_rescue) || 0;
  const maxRedemptionValue = backendMaxRescue / 100;

  // Transform minimum_sales_value_to_redeem (minimum purchase for redemption)
  // Backend: cents (integer)
  // Frontend: decimal
  const backendMinPurchaseRedemption =
    parseFloat(campaign.strategy?.minimum_sales_value_to_redeem) || 0;
  const minimumPurchaseRedemption = backendMinPurchaseRedemption / 100;

  // Transform accumulation limits (from cents to decimal)
  const minimumAccumulation =
    (parseFloat(campaign.strategy?.minimum_accumulation) || 0) / 100;
  const maximumAccumulation = campaign.strategy?.maximum_accumulation
    ? parseFloat(campaign.strategy.maximum_accumulation) / 100
    : null;

  // Transform minimum order for cashback generation (from cents to decimal)
  const minimumOrder =
    (parseFloat(campaign.strategy?.minimum_sales_value_to_generate_cashback) ||
      0) / 100;

  return {
    id: campaign.id,
    name: campaign.name,
    startDate: campaign.start_value
      ? new Date(campaign.start_value * 1000).toISOString()
      : null,
    endDate: campaign.end_value
      ? new Date(campaign.end_value * 1000).toISOString()
      : null,
    status: campaign.status,
    isActive: isCurrentlyActive,
    type: campaign.type,
    // Cashback fields
    cashbackType: cashbackType,
    cashbackValue: cashbackValue,
    // Rescue fields (maximum discount percentage)
    rescueValue: rescueValue,
    // Max redemption value (in R$)
    maxRedemptionValue: maxRedemptionValue,
    // Max redemption type (default to 'fixed' since backend doesn't have this field anymore)
    maxRedemptionType: 'fixed',
    // Minimum purchase for redemption (in R$)
    minimumPurchaseRedemption: minimumPurchaseRedemption,
    // Other strategy fields
    calculationBasis:
      campaign.strategy?.calculation_basis === 'gross'
        ? 'total'
        : campaign.strategy?.calculation_basis || 'gross',
    minimumAccumulation: minimumAccumulation,
    maximumAccumulation: maximumAccumulation,
    minimumOrder: minimumOrder,
    daysToExpire: parseInt(campaign.strategy?.expires_in_days) || 30,
    physicalStoreDays: parseInt(campaign.strategy?.availability_time) || 0,
    ecommerceDays: parseInt(campaign.strategy?.ecommerce_days) || 0,
    blockDiscounts: campaign.strategy?.block_discounts || false,
    blockVtexAnyCoupon: campaign.strategy?.block_vtex_any_coupon || false,
    redemptionBlockDiscounts:
      campaign.strategy?.redemption_block_discounts || false,
    redemptionBlockVtexAnyCoupon:
      campaign.strategy?.redemption_block_vtex_any_coupon || false,
    redemptionBlockVtexPriceRemarked:
      campaign.strategy?.redemption_block_vtex_price_remarked || false,
    // Raw strategy for reference
    strategy: campaign.strategy,
    accountId: campaign.account_id,
    createdAt: campaign.created_at,
    updatedAt: campaign.updated_at,
  };
};

/**
 * Transform campaign data from frontend format to backend format
 *
 * Key transformations:
 * - cashback: percentage (0-100) → backend (0-1), fixed (decimal) → backend (cents)
 * - rescue: display (0-100) → backend (0-1)
 * - maximum_rescue: decimal (R$) → backend (cents, integer)
 * - timestamps: Date/ISO string → seconds (UTC)
 */
export const transformFrontendCampaignToBackend = (campaign) => {
  // Transform cashback value based on type
  // Frontend: percentage = 0-100 (e.g., 20 for 20%), fixed = decimal (e.g., 20.00 for R$ 20.00)
  // Backend: percentage = 0-1 (e.g., 0.20 for 20%), fixed = cents (e.g., 2000 for R$ 20.00)
  const cashbackType = campaign.cashbackType || 'percentage';
  const frontendCashback = parseFloat(campaign.cashbackValue) || 0;

  let backendCashback;
  if (cashbackType === 'percentage') {
    // Frontend: 20 → Backend: 0.20 (with max 2 decimals)
    backendCashback = parseFloat((frontendCashback / 100).toFixed(2));
  } else {
    // Frontend: 20.00 → Backend: 2000 (cents, integer)
    backendCashback = Math.round(frontendCashback * 100);
  }

  // Transform rescue value (maximum discount percentage)
  // Frontend: 0-100 (e.g., 20 for 20%)
  // Backend: 0-1 (e.g., 0.20 for 20%)
  const frontendRescue = parseFloat(campaign.rescueValue) || 0;
  const backendRescue = parseFloat((frontendRescue / 100).toFixed(2));

  // Transform maximum_rescue (maximum redemption value in R$)
  // Frontend: decimal (e.g., 987654 for R$ 987,654.00)
  // Backend: integer cents (e.g., 98765400 for R$ 987,654.00)
  // NOTE: The frontend CurrencyInput returns the value as a number (not cents)
  const frontendMaxRescue = parseFloat(campaign.maxRedemptionValue) || 0;
  const backendMaxRescue = Math.round(frontendMaxRescue * 100);

  // Transform minimum purchase for redemption
  // Frontend: decimal (e.g., 100 for R$ 100.00)
  // Backend: integer cents (e.g., 10000 for R$ 100.00)
  const frontendMinPurchaseRedemption =
    parseFloat(campaign.minimumPurchaseRedemption) || 0;
  const backendMinPurchaseRedemption = Math.round(
    frontendMinPurchaseRedemption * 100
  );

  return {
    name: campaign.name,
    createNotificationConfig: campaign.createNotificationConfig,
    status: campaign.status || 'active',
    type: 'date_range',
    start_value: campaign.startDate
      ? Math.floor(new Date(campaign.startDate).getTime() / 1000)
      : null,
    end_value: campaign.endDate
      ? Math.floor(new Date(campaign.endDate).getTime() / 1000)
      : null,
    strategy: {
      // Cashback fields
      cashback: backendCashback,
      cashback_type: cashbackType,
      // Rescue field (maximum discount percentage) - required
      rescue: backendRescue,
      // Maximum rescue value in cents (integer)
      maximum_rescue: backendMaxRescue,
      // Calculation basis
      calculation_basis:
        campaign.calculationBasis === 'total'
          ? 'gross'
          : campaign.calculationBasis || 'gross',
      // Accumulation limits (in cents)
      minimum_accumulation: Math.round(
        (parseFloat(campaign.minimumAccumulation) || 0) * 100
      ),
      maximum_accumulation: campaign.maximumAccumulation
        ? Math.round(parseFloat(campaign.maximumAccumulation) * 100)
        : null,
      // Order requirements (in cents)
      minimum_sales_value_to_redeem: backendMinPurchaseRedemption,
      minimum_sales_value_to_generate_cashback: Math.round(
        (parseFloat(campaign.minimumOrder) || 0) * 100
      ),
      // Expiration
      expires_in_days: parseInt(campaign.daysToExpire) || 30,
      // Availability
      availability_time: parseInt(campaign.physicalStoreDays) || 0,
      ecommerce_days: parseInt(campaign.ecommerceDays) || 0,
      // Block settings for accumulation
      block_discounts: campaign.blockDiscounts || false,
      block_vtex_any_coupon: campaign.blockVtexAnyCoupon || false,
      // Block settings for redemption
      redemption_block_discounts: campaign.redemptionBlockDiscounts || false,
      redemption_block_vtex_any_coupon:
        campaign.redemptionBlockVtexAnyCoupon || false,
      redemption_block_vtex_price_remarked:
        campaign.redemptionBlockVtexPriceRemarked || false,
      // Active status (required)
      active: true,
    },
  };
};

export const campaignService = {
  async getCampaigns(pageOptions = {}) {
    const { page = 1, limit = 10, order } = pageOptions;
    const response = await api.getCampaigns({ page, limit, order });

    return {
      data: response.data.map(transformBackendCampaignToFrontend),
      meta: {
        currentPage: response.meta.page || 1,
        itemCount: response.meta.take || 10,
        itemsPerPage: response.meta.take || 10,
        totalPages: response.meta.pages || 1,
        totalItems: response.meta.total || 0,
      },
    };
  },

  async getCampaignById(id) {
    const response = await api.getCampaignById(id);
    return transformBackendCampaignToFrontend(response);
  },

  async getActiveCampaign() {
    try {
      const response = await api.getActiveCampaign();
      return transformBackendCampaignToFrontend(response);
    } catch (error) {
      // Return null if no active campaign found
      return null;
    }
  },

  async createCampaign(campaignData) {
    const backendData = transformFrontendCampaignToBackend(campaignData);
    const response = await api.createCampaign(backendData);
    return transformBackendCampaignToFrontend(response);
  },

  async updateCampaign(id, campaignData) {
    // If only status is being updated (pause/reactivate/cancel), don't transform the full object
    if (campaignData.status && Object.keys(campaignData).length === 1) {
      const response = await api.updateCampaign(id, {
        status: campaignData.status,
      });
      return transformBackendCampaignToFrontend(response);
    }

    const backendData = transformFrontendCampaignToBackend(campaignData);
    const response = await api.updateCampaign(id, backendData);
    return transformBackendCampaignToFrontend(response);
  },
};

export default campaignService;

// Export campaigns to CSV format
export const downloadCampaignsCSV = (campaigns, filename = 'campanhas.csv') => {
  const headers = [
    'Nome',
    'Data Início',
    'Data Fim',
    'Cashback',
    'Resgate Máximo',
    'Status',
  ];

  const rows = campaigns.map((campaign) => [
    campaign.name,
    campaign.startDate
      ? new Date(campaign.startDate).toLocaleString('pt-BR')
      : '-',
    campaign.endDate ? new Date(campaign.endDate).toLocaleString('pt-BR') : '-',
    campaign.cashbackType === 'percentage'
      ? `${campaign.cashbackValue}%`
      : `R$ ${campaign.cashbackValue?.toFixed(2) || '0.00'}`,
    campaign.maxRedemptionType === 'percentage'
      ? `${campaign.maxRedemptionValue}%`
      : `R$ ${campaign.maxRedemptionValue?.toFixed(2) || '0.00'}`,
    campaign.isActive ? 'Vigente' : campaign.status,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
